import { Link } from "react-router-dom";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Icon, type IconName } from "@/lib/icons";

export interface ShellAccountLink {
  label: string;
  href: string;
  icon: IconName;
  /** Opens in a new tab, so the panel stays open (the public website). */
  newTab?: boolean;
}

interface ShellUserMenuProps {
  name?: string;
  email?: string;
  roleLabel?: string;
  links: ShellAccountLink[];
  themeLabel: string;
  themeIcon: IconName;
  onToggleTheme: () => void;
  logoutLabel: string;
  onLogout: () => void;
  noEmailLabel: string;
  menuLabel: string;
}

/** The account menu, the same in every space. */
export function ShellUserMenu({
  name,
  email,
  roleLabel,
  links,
  themeLabel,
  themeIcon,
  onToggleTheme,
  logoutLabel,
  onLogout,
  noEmailLabel,
  menuLabel,
}: ShellUserMenuProps) {
  const initial = name?.trim()?.[0]?.toUpperCase() ?? "?";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={menuLabel}
          className="flex items-center gap-1.5 rounded-full p-0.5 pr-1.5 transition-colors hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Avatar className="size-8">
            <AvatarFallback className="bg-surface-2 text-sm font-medium text-foreground">{initial}</AvatarFallback>
          </Avatar>
          <Icon name="chevronDown" className="hidden size-3.5 text-muted-foreground sm:block" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64 rounded-lg border-border/60 bg-popover p-1">
        <DropdownMenuLabel className="px-2.5 py-2 font-normal">
          <p className="truncate text-sm font-medium text-foreground">{name}</p>
          <p className="truncate text-xs text-muted-foreground">{email || noEmailLabel}</p>
          {roleLabel ? (
            <span className="mt-1.5 inline-block rounded-full bg-surface-2 px-2 py-0.5 text-[11px] text-muted-foreground">{roleLabel}</span>
          ) : null}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {links.map((link) => (
          <DropdownMenuItem key={link.href} asChild className="cursor-pointer gap-2.5 text-[13px]">
            <Link to={link.href} {...(link.newTab ? { target: "_blank", rel: "noopener" } : {})}>
              <Icon name={link.icon} className="size-4 text-muted-foreground" />
              {link.label}
            </Link>
          </DropdownMenuItem>
        ))}
        <DropdownMenuItem onClick={onToggleTheme} className="cursor-pointer gap-2.5 text-[13px]">
          <Icon name={themeIcon} className="size-4 text-muted-foreground" />
          {themeLabel}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onLogout} className="cursor-pointer gap-2.5 text-[13px] text-danger focus:text-danger">
          <Icon name="logOut" className="size-4" />
          {logoutLabel}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
